// Real-time trigger notification listener for the web dashboard.
// Opens the notification WebSocket and surfaces incoming trigger events as a
// stacked snackbar toast, an optional sound, and an optional browser notification.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Collapse, Stack } from '@mui/material';
import { buildNotificationWsUrl, getNotificationPreferences } from '../../services/notificationService';
import { playNotificationSound, primeNotificationSound } from '../../utils/notificationSound';
import {
  DashboardAlertPrefs,
  TriggerEventPrefs,
  TriggerNotificationMessage,
} from '../../types/notifications';

interface NotificationCenterProps {
  tenantId: string;
  userId: number;
  token: string;
}

interface ActiveToast {
  id: number;
  title: string;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const MAX_VISIBLE_TOASTS = 4;

/** Event emitted by the settings page after the user saves; triggers a prefs refetch. */
export const NOTIFICATION_PREFS_UPDATED_EVENT = 'branchrouter:notification-prefs-updated';

/**
 * Event other components can dispatch to show an in-app notification locally,
 * without a server round-trip. Used by the "Send test notification" button so the
 * popup + sound are verifiable even if the realtime WebSocket isn't connected.
 * detail: { title, message, severity }
 */
export const NOTIFICATION_LOCAL_EVENT = 'branchrouter:local-notification';

/** Convenience helper to raise a local in-app notification (popup + sound). */
export function showLocalNotification(
  title: string,
  message: string,
  severity: 'success' | 'error' | 'info' = 'info'
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_LOCAL_EVENT, { detail: { title, message, severity } })
  );
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ tenantId, userId, token }) => {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const dashboardPrefsRef = useRef<DashboardAlertPrefs | null>(null);
  const eventPrefsRef = useRef<TriggerEventPrefs | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttempts = useRef(0);
  const manualClose = useRef(false);
  const nextId = useRef(1);

  const loadPrefs = useCallback(async () => {
    try {
      const prefs = await getNotificationPreferences(token);
      dashboardPrefsRef.current = prefs.dashboard_alerts;
      eventPrefsRef.current = prefs.trigger_events;
    } catch {
      // Defaults: show UI notifications even if prefs can't be loaded.
      dashboardPrefsRef.current = {
        enabled: true,
        sound_enabled: true,
        popup_enabled: true,
        alert_types: ['error', 'warning', 'info', 'success'],
      };
      eventPrefsRef.current = {
        indicator_activated: true,
        execution_fired: true,
        execution_failed: true,
      };
    }
  }, [token]);

  const pushToast = useCallback((msg: TriggerNotificationMessage) => {
    const dash = dashboardPrefsRef.current;
    const events = eventPrefsRef.current;
    if (dash && dash.enabled === false) return;

    // Honor per-event mute toggles (test events always show).
    if (msg.event && msg.event !== 'test' && events) {
      const enabledForEvent =
        msg.event === 'indicator_deactivated'
          ? true
          : (events as unknown as Record<string, boolean>)[msg.event];
      if (enabledForEvent === false) return;
    }

    const severity: 'success' | 'error' | 'info' =
      msg.severity === 'success' ? 'success' : msg.severity === 'error' ? 'error' : 'info';
    const title = msg.title || 'Branch Router';
    const message = msg.message || '';

    const id = nextId.current++;
    setToasts((prev) => {
      const next = [...prev, { id, title, message, severity }];
      return next.slice(-MAX_VISIBLE_TOASTS);
    });
    // Auto-dismiss after a while (errors linger longer).
    const ttl = severity === 'error' ? 12000 : 7000;
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), ttl);

    // Sound
    if (!dash || dash.sound_enabled !== false) {
      playNotificationSound(severity);
    }

    // Browser notification (when tab not focused and permitted)
    if (
      (!dash || dash.popup_enabled !== false) &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted' &&
      typeof document !== 'undefined' &&
      document.hidden
    ) {
      try {
        // eslint-disable-next-line no-new
        new Notification(title, { body: message });
      } catch {
        /* ignore */
      }
    }
  }, []);

  const handleClose = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Connect WebSocket (with reconnect + heartbeat)
  useEffect(() => {
    if (!tenantId || !userId) return;
    manualClose.current = false;

    const clearTimers = () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
    };

    const connect = () => {
      let ws: WebSocket;
      try {
        ws = new WebSocket(buildNotificationWsUrl(tenantId, userId, token));
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        // eslint-disable-next-line no-console
        console.info('[notifications] realtime channel connected');
        heartbeatTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data: TriggerNotificationMessage = JSON.parse(event.data);
          if (data.type === 'trigger_notification') {
            pushToast(data);
          }
        } catch {
          /* ignore malformed message */
        }
      };

      ws.onclose = () => {
        if (heartbeatTimer.current) {
          clearInterval(heartbeatTimer.current);
          heartbeatTimer.current = null;
        }
        if (!manualClose.current) {
          // eslint-disable-next-line no-console
          console.warn('[notifications] realtime channel closed; reconnecting…');
          scheduleReconnect();
        }
      };

      ws.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn('[notifications] realtime channel error');
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      };
    };

    const scheduleReconnect = () => {
      if (manualClose.current) return;
      reconnectAttempts.current = Math.min(reconnectAttempts.current + 1, 6);
      const delay = Math.min(1000 * 2 ** (reconnectAttempts.current - 1), 30000);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    loadPrefs();
    connect();

    const onPrefsUpdated = () => loadPrefs();
    window.addEventListener(NOTIFICATION_PREFS_UPDATED_EVENT, onPrefsUpdated);

    return () => {
      manualClose.current = true;
      clearTimers();
      window.removeEventListener(NOTIFICATION_PREFS_UPDATED_EVENT, onPrefsUpdated);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [tenantId, userId, token, loadPrefs, pushToast]);

  // Local in-app notifications (e.g. from the "Send test notification" button).
  // These render a popup + sound without needing the realtime WebSocket.
  useEffect(() => {
    const onLocal = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      pushToast({
        type: 'trigger_notification',
        event: 'test',
        severity: detail.severity === 'success' || detail.severity === 'error' ? detail.severity : 'info',
        title: detail.title || 'Branch Router',
        message: detail.message || '',
      });
    };
    window.addEventListener(NOTIFICATION_LOCAL_EVENT, onLocal);
    return () => window.removeEventListener(NOTIFICATION_LOCAL_EVENT, onLocal);
  }, [pushToast]);

  // Prime audio + request browser-notification permission on first user gesture.
  useEffect(() => {
    const onFirstGesture = () => {
      primeNotificationSound();
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'default' &&
        (!dashboardPrefsRef.current || dashboardPrefsRef.current.popup_enabled !== false)
      ) {
        Notification.requestPermission().catch(() => {});
      }
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture);
    window.addEventListener('keydown', onFirstGesture);
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
  }, []);

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'fixed',
        top: 80,
        right: 16,
        zIndex: (t) => t.zIndex.snackbar + 1,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => (
        <Collapse key={toast.id} in appear>
          <Box sx={{ pointerEvents: 'auto' }}>
            <Alert
              severity={toast.severity === 'warning' ? 'warning' : toast.severity}
              variant="filled"
              onClose={() => handleClose(toast.id)}
              sx={{ width: 360, boxShadow: 4 }}
            >
              <strong>{toast.title}</strong>
              {toast.message ? (
                <div style={{ marginTop: 4, fontWeight: 400 }}>{toast.message}</div>
              ) : null}
            </Alert>
          </Box>
        </Collapse>
      ))}
    </Stack>
  );
};

export default NotificationCenter;
